<template>
  <div>
    <slot :data="data" :status="status" :error="error"></slot>
  </div>
</template>

<script setup lang="ts">
import { isEqual } from "radash";

const props = withDefaults(
  defineProps<{
    querySetGroupId: number | null;
    filters?: Indexable;
    context?: Indexable;
    pageNumber?: number;
    pageSize?: number;
    watch?: boolean;
  }>(),
  {
    filters: () => ({}),
    context: () => ({}),
    pageNumber: 1,
    pageSize: 20,
    watch: true,
  }
);
const {
  data,
  status,
  error,
  fetchData,
  filters,
  context,
  pageNumber,
  pageSize,
  querySetGroupId,
} = useQuerySet(props.querySetGroupId);

// Plain-data copy, so a later in-place mutation of the caller's object is detectable.
// toRaw/JSON keeps it safe for the reactive proxies these props usually are.
function snapshot(value: Indexable): Indexable {
  try {
    return JSON.parse(JSON.stringify(value ?? {}));
  } catch {
    return { ...(value ?? {}) };
  }
}

watch(
  () => [props.filters, props.context, props.pageNumber, props.pageSize],
  () => {
    if (
      isEqual(props.filters, filters.value) &&
      isEqual(props.context, context.value) &&
      props.pageNumber === pageNumber.value &&
      props.pageSize === pageSize.value
    ) {
      return;
    }

    querySetGroupId.value = props.querySetGroupId;
    // Snapshot rather than storing the caller's objects by reference. The form passed as
    // context is mutated in place - useRecord.save() assigns the new primary key straight onto
    // it - so keeping the reference made the isEqual guard above compare an object with itself
    // and short-circuit every time, leaving the data stuck on the first fetch.
    filters.value = snapshot(props.filters);
    context.value = snapshot(props.context);
    pageNumber.value = props.pageNumber;
    pageSize.value = props.pageSize;
    fetchData();
  },
  { immediate: true, deep: true }
);
</script>

<style scoped></style>
